import React, { useState, useCallback, lazy, Suspense } from 'react';
import FileUpload from './components/FileUpload';
import MortalityDashboard from './components/MortalityDashboard';
import DataFilters from './components/DataFilters';
import { parseSAPS3Data, calculateMortalityStats, calculateCalibration } from './utils/saps3Utils';
import { Activity, BarChart3, FileText, TrendingUp, Users, AlertCircle, Target, Brain, Clock, Calculator } from 'lucide-react';

// Lazy loading para componentes pesados
const MortalityCharts = lazy(() => import('./components/MortalityCharts'));
const RiskStratificationDashboard = lazy(() => import('./components/RiskStratificationDashboard'));
const TrendsAnalysisDashboard = lazy(() => import('./components/TrendsAnalysisDashboard'));
const UTITemporalAnalysis = lazy(() => import('./components/UTITemporalAnalysis'));
const LengthOfStayCalculator = lazy(() => import('./components/LengthOfStayCalculator'));
const MachineLearningModels = lazy(() => import('./components/MachineLearningModels'));

function App() {
  const [rawData, setRawData] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [stats, setStats] = useState(null);
  const [calibrationData, setCalibrationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [trainedModel, setTrainedModel] = useState(null);

  const processData = useCallback((data) => {
    if (!data || data.length === 0) {
      setStats(null);
      setCalibrationData([]);
      return;
    }

    const newStats = calculateMortalityStats(data);
    const newCalibrationData = calculateCalibration(data);
    
    setStats(newStats);
    setCalibrationData(newCalibrationData);
  }, []);

  const handleDataLoad = useCallback((csvData) => {
    setLoading(true);
    try {
      const parsedData = parseSAPS3Data(csvData);
      setRawData(parsedData);
      setFilteredData(parsedData);
      processData(parsedData);
    } catch (error) {
      console.error('Error processing data:', error);
    } finally {
      setLoading(false);
    }
  }, [processData]);

  const handleFilterChange = useCallback((filteredData, filters) => {
    setFilteredData(filteredData);
    processData(filteredData);
  }, [processData]);

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: Activity },
    { id: 'charts', name: 'Gráficos', icon: BarChart3 },
    { id: 'risk', name: 'Estratificação', icon: Target },
    { id: 'trends', name: 'Tendências', icon: TrendingUp },
    { id: 'uti', name: 'Análise UTI', icon: Clock },
    { id: 'calculator', name: 'Calculadora LOS', icon: Calculator },
    { id: 'ml', name: 'Machine Learning', icon: Brain },
    { id: 'data', name: 'Dados', icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-unimed-beige-50 to-unimed-lightblue-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-unimed-green-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="unimed-gradient-primary p-3 rounded-xl mr-4">
                <Activity className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="unimed-titulo-principal text-3xl text-unimed-darkgreen-800">
                  SAPS3 Analytics Unimed
                </h1>
                <p className="unimed-chapeu text-sm text-unimed-darkgreen-600">Dashboard Executivo de Mortalidade em UTI</p>
              </div>
            </div>
            {stats && (
              <div className="text-right bg-gradient-to-r from-unimed-green-50 to-unimed-lightblue-50 px-6 py-4 rounded-xl border border-unimed-green-200">
                <p className="unimed-chapeu text-sm text-unimed-darkgreen-600">Total de Pacientes</p>
                <p className="unimed-titulo-principal text-3xl text-unimed-green-600">{stats.totalPatients.toLocaleString()}</p>
                <div className="flex items-center justify-end mt-1">
                  <Users className="h-4 w-4 text-unimed-green-500 mr-1" />
                  <span className="unimed-texto-corrido text-xs text-unimed-darkgreen-500">Análise ativa</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!rawData ? (
          /* Upload Section */
          <div className="text-center animate-fade-in">
            <div className="max-w-4xl mx-auto">
              {/* Hero Section */}
              <div className="unimed-section mb-12">
                <div className="flex justify-center mb-6">
                  <div className="unimed-gradient-primary p-6 rounded-2xl shadow-lg">
                    <TrendingUp className="h-12 w-12 text-white" />
                  </div>
                </div>
                <h2 className="unimed-titulo-principal text-4xl text-unimed-darkgreen-800 mb-4">
                  Analytics Avançado para UTI Unimed
                </h2>
                <p className="unimed-texto-corrido text-xl text-unimed-darkgreen-600 mb-8 max-w-2xl mx-auto">
                  Transforme seus dados SAPS3 em insights acionáveis que reflitam o cuidado e a humanidade da Unimed
                </p>
              </div>

              {/* Upload Card */}
              <div className="unimed-card unimed-section mb-12">
                <div className="flex items-center justify-center mb-6">
                  <div className="bg-unimed-green-100 p-3 rounded-xl mr-4">
                    <FileText className="h-8 w-8 text-unimed-green-600" />
                  </div>
                  <h3 className="unimed-titulo-principal text-2xl text-unimed-darkgreen-800">
                    Importe seus dados SAPS3
                  </h3>
                </div>
                <FileUpload onDataLoad={handleDataLoad} loading={loading} />
                
                <div className="mt-6 text-center">
                  <p className="unimed-texto-corrido text-sm text-unimed-darkgreen-500">
                    Formatos suportados: CSV com colunas atendimento, dataAtendimento, saps3Score, saps3Percent, outcome
                  </p>
                </div>
              </div>
              
              {/* Features Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                <div className="unimed-card-hover p-6 text-center">
                  <div className="bg-unimed-green-100 p-3 rounded-xl w-fit mx-auto mb-4">
                    <AlertCircle className="h-8 w-8 text-unimed-green-600" />
                  </div>
                  <h4 className="unimed-titulo-secundario text-unimed-darkgreen-800 mb-2">SMR Analysis</h4>
                  <p className="unimed-texto-corrido text-sm text-unimed-darkgreen-600">Standardized Mortality Ratio com intervalos de confiança</p>
                </div>
                
                <div className="unimed-card-hover p-6 text-center">
                  <div className="bg-unimed-orange-100 p-3 rounded-xl w-fit mx-auto mb-4">
                    <BarChart3 className="h-8 w-8 text-unimed-orange-600" />
                  </div>
                  <h4 className="unimed-titulo-secundario text-unimed-darkgreen-800 mb-2">Visualizações</h4>
                  <p className="unimed-texto-corrido text-sm text-unimed-darkgreen-600">Gráficos interativos e dashboards executivos</p>
                </div>
                
                <div className="unimed-card-hover p-6 text-center">
                  <div className="bg-unimed-lime-100 p-3 rounded-xl w-fit mx-auto mb-4">
                    <Users className="h-8 w-8 text-unimed-lime-700" />
                  </div>
                  <h4 className="unimed-titulo-secundario text-unimed-darkgreen-800 mb-2">Segmentação</h4>
                  <p className="unimed-texto-corrido text-sm text-unimed-darkgreen-600">Análise por faixas de risco e perfis de paciente</p>
                </div>
                
                <div className="unimed-card-hover p-6 text-center">
                  <div className="bg-unimed-lightblue-100 p-3 rounded-xl w-fit mx-auto mb-4">
                    <TrendingUp className="h-8 w-8 text-unimed-lightblue-700" />
                  </div>
                  <h4 className="unimed-titulo-secundario text-unimed-darkgreen-800 mb-2">Tendências</h4>
                  <p className="unimed-texto-corrido text-sm text-unimed-darkgreen-600">Acompanhamento temporal da performance</p>
                </div>
              </div>

              {/* Information Section */}
              <div className="bg-gradient-to-r from-unimed-green-50 to-unimed-lightblue-50 rounded-2xl unimed-section border border-unimed-green-200">
                <div className="flex items-center mb-6">
                  <div className="unimed-gradient-primary p-3 rounded-xl mr-4">
                    <Activity className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="unimed-titulo-principal text-2xl text-unimed-darkgreen-800">Sobre o SAPS3</h3>
                </div>
                <div className="unimed-texto-corrido text-unimed-darkgreen-700 space-y-4 text-left">
                  <p className="text-lg leading-relaxed">
                    O <span className="unimed-destaque">SAPS 3 (Simplified Acute Physiology Score 3)</span> é 
                    o padrão internacional para avaliação de gravidade em UTI, refletindo o compromisso da Unimed com excelência médica.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <h4 className="unimed-subtitulo font-semibold text-unimed-green-700 mb-3 flex items-center">
                        <div className="w-2 h-2 bg-unimed-green-500 rounded-full mr-2"></div>
                        Métricas Principais
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start">
                          <span className="text-unimed-green-500 mr-2">✓</span>
                          <span><strong>SMR:</strong> Razão de Mortalidade Padronizada</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-unimed-green-500 mr-2">✓</span>
                          <span><strong>Calibração:</strong> Acurácia das predições</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-unimed-green-500 mr-2">✓</span>
                          <span><strong>Discriminação:</strong> Separação de riscos</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="unimed-subtitulo font-semibold text-unimed-orange-700 mb-3 flex items-center">
                        <div className="w-2 h-2 bg-unimed-orange-500 rounded-full mr-2"></div>
                        Insights Avançados
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start">
                          <span className="text-unimed-orange-500 mr-2">✓</span>
                          <span>Análise por faixas de risco</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-unimed-orange-500 mr-2">✓</span>
                          <span>Tendências temporais</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-unimed-orange-500 mr-2">✓</span>
                          <span>Benchmarking de performance</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Navigation Tabs */}
            <div className="mb-6">
              <div className="border-b border-unimed-beige-200">
                <nav className="-mb-px flex space-x-8">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center py-3 px-4 border-b-2 font-medium text-sm transition-colors duration-200 ${
                          activeTab === tab.id
                            ? 'border-unimed-green-500 text-unimed-green-600 bg-unimed-green-50'
                            : 'border-transparent text-unimed-darkgreen-500 hover:text-unimed-green-600 hover:border-unimed-green-300'
                        }`}
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        <span className="unimed-texto-corrido">{tab.name}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Filters */}
            <DataFilters 
              data={rawData} 
              onFilterChange={handleFilterChange}
            />

            {/* Content based on active tab */}
            <Suspense fallback={
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-unimed-green-500"></div>
                <span className="unimed-texto-corrido ml-2 text-unimed-darkgreen-600">Carregando...</span>
              </div>
            }>
              {activeTab === 'dashboard' && (
                <MortalityDashboard 
                  data={filteredData} 
                  stats={stats} 
                />
              )}

              {activeTab === 'charts' && (
                <MortalityCharts 
                  data={filteredData} 
                  stats={stats} 
                  calibrationData={calibrationData}
                />
              )}

              {activeTab === 'risk' && (
                <RiskStratificationDashboard 
                  data={filteredData}
                />
              )}

              {activeTab === 'trends' && (
                <TrendsAnalysisDashboard 
                  data={filteredData}
                />
              )}

              {activeTab === 'uti' && (
                <UTITemporalAnalysis 
                  data={filteredData}
                />
              )}

              {activeTab === 'calculator' && (
                <LengthOfStayCalculator 
                  data={filteredData}
                  trainedModel={trainedModel}
                />
              )}

              {activeTab === 'ml' && (
                <MachineLearningModels 
                  data={filteredData}
                  onModelTrained={setTrainedModel}
                />
              )}
            </Suspense>

            {activeTab === 'data' && (
              <div className="unimed-card unimed-section">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="unimed-titulo-principal text-xl text-unimed-darkgreen-800">DADOS PROCESSADOS</h3>
                  <div className="unimed-texto-corrido text-sm text-unimed-darkgreen-600">
                    {filteredData?.length || 0} de {rawData?.length || 0} registros
                  </div>
                </div>
                
                {filteredData && filteredData.length > 0 && (
                  <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-unimed-beige-200">
                      <thead className="bg-unimed-green-50">
                        <tr>
                          <th className="px-6 py-3 text-left unimed-chapeu text-xs font-semibold text-unimed-darkgreen-700 uppercase tracking-wider">
                            ATENDIMENTO
                          </th>
                          <th className="px-6 py-3 text-left unimed-chapeu text-xs font-semibold text-unimed-darkgreen-700 uppercase tracking-wider">
                            DATA
                          </th>
                          <th className="px-6 py-3 text-left unimed-chapeu text-xs font-semibold text-unimed-darkgreen-700 uppercase tracking-wider">
                            SAPS3 SCORE
                          </th>
                          <th className="px-6 py-3 text-left unimed-chapeu text-xs font-semibold text-unimed-darkgreen-700 uppercase tracking-wider">
                            % SAPS3
                          </th>
                          <th className="px-6 py-3 text-left unimed-chapeu text-xs font-semibold text-unimed-darkgreen-700 uppercase tracking-wider">
                            DESFECHO
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-unimed-beige-200">
                        {filteredData.slice(0, 100).map((patient, index) => ( // Show first 100 rows
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-unimed-beige-50'}>
                            <td className="px-6 py-4 whitespace-nowrap unimed-texto-corrido text-sm font-medium text-unimed-darkgreen-800">
                              {patient.atendimento}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap unimed-texto-corrido text-sm text-unimed-darkgreen-700">
                              {patient.dataAtendimento ? new Date(patient.dataAtendimento).toLocaleDateString('pt-BR') : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap unimed-texto-corrido text-sm text-unimed-darkgreen-700">
                              {patient.saps3Score}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap unimed-texto-corrido text-sm text-unimed-darkgreen-700">
                              {patient.saps3Percentage.toFixed(1)}%
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 unimed-texto-corrido text-xs font-semibold rounded-full ${
                                patient.deceased 
                                  ? 'bg-unimed-orange-100 text-unimed-orange-800' 
                                  : 'bg-unimed-green-100 text-unimed-green-800'
                              }`}>
                                {patient.deceased ? 'Óbito' : 'Sobrevivente'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredData.length > 100 && (
                      <p className="unimed-texto-corrido text-sm text-unimed-darkgreen-600 mt-4">
                        Mostrando apenas os primeiros 100 registros de {filteredData.length} total.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Reset button */}
            <div className="mt-8 text-center">
              <button
                onClick={() => {
                  setRawData(null);
                  setFilteredData(null);
                  setStats(null);
                  setCalibrationData([]);
                  setActiveTab('dashboard');
                }}
                className="unimed-cta"
              >
                <FileText className="h-4 w-4 mr-2" />
                Carregar Novo Arquivo
              </button>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t-4 border-unimed-green-500 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="unimed-gradient-primary p-2 rounded-lg">
                <Activity className="h-5 w-5 text-white" />
              </div>
            </div>
            <p className="unimed-texto-corrido text-sm text-unimed-darkgreen-600 font-medium">
              SAPS3 Analytics Unimed - Cuidado que transforma vidas
            </p>
            <p className="unimed-texto-corrido text-xs text-unimed-darkgreen-500 mt-2">
              Baseado no modelo SAPS 3 (Simplified Acute Physiology Score 3) · Desenvolvido com humanidade e tecnologia
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
